/**
 * @fileoverview Authentication Context for EvalOps Application
 * 
 * This module provides comprehensive authentication functionality using WorkOS AuthKit
 * with secure cookie-based JWT token management. It manages the complete authentication
 * lifecycle including login, logout, session persistence, and token management.
 * 
 * Architecture:
 * - WorkOS AuthKit handles OAuth flows and user management
 * - Secure HttpOnly cookie-based JWT token storage
 * - Automatic session validation and cleanup
 * - Comprehensive error handling and logging
 * 
 * Security Features:
 * - HttpOnly, Secure, SameSite=Strict cookie storage (XSS protection)
 * - Short-lived tokens with automatic refresh
 * - CSRF protection for state-changing operations
 * - Bearer token injection for API requests
 * - Protection against token tampering
 * 
 * @example
 * // Basic usage in app root
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * 
 * @example
 * // Using authentication in components
 * const { user, isAuthenticated, login, logout } = useAuth();
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useAuth as useWorkOSAuth, AuthKitProvider } from '@workos-inc/authkit-react';
import { ApiProvider } from '@/lib/contexts/ApiContext';
import { API_BASE_URL } from '@/config/api';
import { identifyUser } from '@/lib/koala';
import * as Sentry from '@sentry/react';
import { authEvents, AuthEvent } from './authEvents';

/**
 * Raw WorkOS user type from the WorkOS AuthKit SDK
 * 
 * This interface defines the structure of user data received directly
 * from WorkOS. It includes organization membership information and
 * various optional fields that may be present depending on the
 * authentication method and organization configuration.
 */
interface RawWorkOSUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  organization_membership?: {
    organization_id: string;
    role: {
      slug: string;
    };
  };
  access_token?: string;
  token?: string;
  [key: string]: unknown; // Allow for additional properties
}

/**
 * Organization data interface
 */
interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

/**
 * Normalized WorkOS user interface
 * 
 * This interface provides a consistent user data structure that normalizes
 * the raw WorkOS data into a more convenient format for use throughout
 * the application.
 */
interface WorkOSUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizations?: Organization[];
  raw_user: RawWorkOSUser;
  raw_token?: {
    access_token: string;
    token_type: string;
    expires_in: number;
  };
}

/**
 * Authentication context interface
 * 
 * This interface defines the shape of the authentication context that is
 * provided to all child components. It includes user state, loading states,
 * and authentication actions.
 */
interface AuthContextType {
  user: WorkOSUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: () => void;
  logout: () => void;
  error?: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Component
 * 
 * This component wraps the application and provides authentication context
 * to all child components. It initializes WorkOS AuthKit and manages the
 * authentication state throughout the application lifecycle.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID;
  const AUTH_CALLBACK_URL = import.meta.env.VITE_AUTH_CALLBACK_URL;
  const IS_DEV = import.meta.env.DEV;
  const IS_PRODUCTION = import.meta.env.PROD;

  if (import.meta.env.DEV) {
    console.log('[Auth] Initializing AuthProvider:', {
      hasClientId: !!WORKOS_CLIENT_ID,
      callbackUrl: AUTH_CALLBACK_URL,
      isDev: IS_DEV,
      isProduction: IS_PRODUCTION
    });
  }

  if (!WORKOS_CLIENT_ID) {
    console.error('[Auth] Missing WORKOS_CLIENT_ID environment variable');
    return <div>Error: Authentication configuration is missing</div>;
  }

  if (!AUTH_CALLBACK_URL) {
    console.error('[Auth] Missing AUTH_CALLBACK_URL environment variable');
    return <div>Error: Authentication callback URL is missing</div>;
  }

  return (
    <ApiProvider>
      <AuthKitProvider
        clientId={WORKOS_CLIENT_ID}
        redirectUri={AUTH_CALLBACK_URL}
        devMode={IS_DEV && !IS_PRODUCTION}
      >
        <AuthContextContent>{children}</AuthContextContent>
      </AuthKitProvider>
    </ApiProvider>
  );
}

/**
 * Authentication Context Content Component
 * 
 * This component contains the core authentication logic and state management.
 * It handles WorkOS integration, session restoration from secure cookies,
 * and provides the authentication context to child components.
 */
/**
 * Creates a fallback user object when complete user data is unavailable
 * but we have a valid session with userId
 */
function createFallbackUser(sessionData: { userId: string; email?: string; firstName?: string; lastName?: string }): WorkOSUser {
  if (import.meta.env.DEV) {
    console.log('[Auth] Creating fallback user from session data:', {
      userId: sessionData.userId,
      hasEmail: !!sessionData.email,
      hasFirstName: !!sessionData.firstName,
      hasLastName: !!sessionData.lastName
    });
  }
  
  const fallbackUser = {
    id: sessionData.userId,
    email: sessionData.email || `user_${sessionData.userId}@example.com`, // Fallback email
    firstName: sessionData.firstName || undefined,
    lastName: sessionData.lastName || undefined,
    raw_user: {
      id: sessionData.userId,
      email: sessionData.email || `user_${sessionData.userId}@example.com`,
      first_name: sessionData.firstName,
      last_name: sessionData.lastName,
    } as RawWorkOSUser
  };
  
  if (import.meta.env.DEV) {
    console.log('[Auth] Fallback user created successfully');
  }
  return fallbackUser;
}

function AuthContextContent({ children }: { children: ReactNode }) {
  const workOSAuth = useWorkOSAuth();
  const { user, isLoading, signIn, signOut } = workOSAuth;
  const [localUser, setLocalUser] = useState<WorkOSUser | null>(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Infinite loop detection
  const [effectCounts, setEffectCounts] = useState<{[key: string]: number}>({});
  const trackEffect = useCallback((effectName: string) => {
    setEffectCounts(prev => {
      const newCount = (prev[effectName] || 0) + 1;
      const newCounts = { ...prev, [effectName]: newCount };
      
      // Alert if any effect runs too many times
      if (newCount > 50) {
        const errorMessage = `[AUTH INFINITE LOOP] ${effectName} has run ${newCount} times! Potential infinite loop detected.`;
        console.error(errorMessage, {
          effectCounts: newCounts,
          effectName,
          location: window.location.href
        });
        
        Sentry.captureException(new Error(errorMessage), {
          tags: { 
            error_type: 'infinite_loop_detected',
            effect_name: effectName,
            critical: 'true'
          },
          contexts: {
            auth_state: {
              effectCounts: newCounts,
              effectName,
              url: window.location.href
            }
          },
          level: 'fatal'
        });
      }
      
      return newCounts;
    });
  }, []); // Remove all dependencies to make it stable

  // Enhanced debugging of WorkOS SDK state with Sentry tracking
  if (import.meta.env.DEV) {
    console.log('[Auth] WorkOS SDK state:', {
      hasUser: !!user,
      isLoading,
      userType: user ? typeof user : 'undefined',
      location: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      localUserExists: !!localUser
    });
  }

  // Send authentication state to Sentry for debugging
  useEffect(() => {
    Sentry.setContext('auth_state', {
      hasWorkOSUser: !!user,
      hasLocalUser: !!localUser,
      isLoading,
      isRestoringSession,
      effectCounts,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
  }, [user, localUser, isLoading, isRestoringSession, effectCounts]);

  /**
   * Session Restoration Effect
   * 
   * This effect runs on component mount to restore the user session from
   * secure HttpOnly cookies. It validates the session with the backend
   * and restores the user state if a valid session exists.
   */
  useEffect(() => {
    trackEffect('session_restoration');
    
    if (import.meta.env.DEV) {
      console.log('[Auth] Starting session restoration from secure cookies', {
        isRestoringSession,
        hasLocalUser: !!localUser,
        hasWorkOSUser: !!user
      });
    }
    const abortController = new AbortController();
    
    const restoreSession = async () => {
      try {
        // Skip session restoration if we already have a user and this isn't a manual trigger
        if (localUser && isRestoringSession === false) {
          if (import.meta.env.DEV) {
            console.log('[Auth] Skipping session restoration - already have local user');
          }
          return;
        }
        
        // Validate session with backend using HttpOnly cookies
        if (import.meta.env.DEV) {
          console.log('[Auth] Validating session with backend');
        }
        const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
          method: 'GET',
          credentials: 'include', // Include HttpOnly cookies
          signal: abortController.signal
        });

        // Check if aborted before processing response
        if (abortController.signal.aborted) {
          if (import.meta.env.DEV) console.log('[Auth] Session validation aborted');
          return;
        }

        if (response.ok) {
          const responseText = await response.text();
          
          // Check if aborted after reading response
          if (abortController.signal.aborted) {
            if (import.meta.env.DEV) console.log('[Auth] Session validation aborted after response');
            return;
          }

          if (import.meta.env.DEV) {
            console.log('[Auth] Session validation response:', {
              status: response.status,
              hasContent: !!responseText,
              contentLength: responseText.length,
              firstChars: responseText.substring(0, 100)
            });
          }

          let sessionData;
          try {
            sessionData = responseText ? JSON.parse(responseText) : null;
          } catch (parseError) {
            console.warn('[Auth] Failed to parse session response as JSON, treating as empty:', parseError);
            sessionData = null;
          }

          if (sessionData?.success && sessionData.data?.user) {
            if (import.meta.env.DEV) {
              console.log('[Auth] Valid session found with complete user data');
            }
            // Ensure we have the proper user structure with organizations
            const userData = sessionData.data.user;
            const sessionUser: WorkOSUser = {
              id: userData.id,
              email: userData.email,
              firstName: userData.firstName || userData.name?.split(' ')[0],
              lastName: userData.lastName || userData.name?.split(' ').slice(1).join(' '),
              organizations: userData.organizations || [],
              raw_user: userData.raw_user || {
                id: userData.id,
                email: userData.email,
                first_name: userData.firstName,
                last_name: userData.lastName,
              } as RawWorkOSUser
            };
            setLocalUser(sessionUser);
            
            // Identify user in Koala on session restoration
            identifyUser(sessionUser.email, {
              name: `${sessionUser.firstName || ''} ${sessionUser.lastName || ''}`.trim(),
              firstName: sessionUser.firstName,
              lastName: sessionUser.lastName,
              organizationId: sessionUser.organizations?.[0]?.id,
              isAuthenticated: true
            });
          } else if (sessionData?.success && sessionData.data?.userId) {
            // Handle userId-only response format
            if (import.meta.env.DEV) {
              console.log('[Auth] Valid session found with userId, attempting to fetch complete user data');
            }
            
            try {
              if (import.meta.env.DEV) {
                console.log(`[Auth] Fetching user data from: ${API_BASE_URL}/api/users/${sessionData.data.userId}`);
              }
              const userResponse = await fetch(`${API_BASE_URL}/api/users/${sessionData.data.userId}`, {
                credentials: 'include',
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
                }
              });

              if (import.meta.env.DEV) {
                console.log('[Auth] User fetch response:', {
                  status: userResponse.status,
                  statusText: userResponse.statusText,
                  ok: userResponse.ok
                });
              }

              if (userResponse.ok) {
                const userResponseText = await userResponse.text();
                if (import.meta.env.DEV) {
                  console.log('[Auth] User response text:', userResponseText);
                }
                
                let userData;
                try {
                  userData = userResponseText ? JSON.parse(userResponseText) : null;
                } catch (parseError) {
                  console.warn('[Auth] Failed to parse user response JSON:', parseError);
                  userData = null;
                }
                
                if (userData?.success && userData.data) {
                  if (import.meta.env.DEV) {
                    console.log('[Auth] Successfully fetched complete user data');
                  }
                  // Ensure user data has the correct structure
                  const fetchedUser: WorkOSUser = {
                    id: userData.data.id,
                    email: userData.data.email,
                    firstName: userData.data.firstName || userData.data.name?.split(' ')[0],
                    lastName: userData.data.lastName || userData.data.name?.split(' ').slice(1).join(' '),
                    organizations: userData.data.organizations || [],
                    raw_user: userData.data.raw_user || {
                      id: userData.data.id,
                      email: userData.data.email,
                      first_name: userData.data.firstName,
                      last_name: userData.data.lastName,
                    } as RawWorkOSUser
                  };
                  setLocalUser(fetchedUser);
                  
                  // Identify user in Koala after fetching complete data
                  identifyUser(fetchedUser.email, {
                    name: `${fetchedUser.firstName || ''} ${fetchedUser.lastName || ''}`.trim(),
                    firstName: fetchedUser.firstName,
                    lastName: fetchedUser.lastName,
                    organizationId: fetchedUser.organizations?.[0]?.id || userData.data.raw_user?.organization_membership?.organization_id,
                    isAuthenticated: true
                  });
                } else {
                  console.warn('[Auth] User endpoint returned invalid data, creating fallback user');
                  setLocalUser(createFallbackUser(sessionData.data));
                }
              } else {
                console.warn(`[Auth] User endpoint returned ${userResponse.status}, creating fallback user`);
                setLocalUser(createFallbackUser(sessionData.data));
              }
            } catch (fetchError) {
              console.warn('[Auth] Failed to fetch user data, creating fallback user:', fetchError);
              setLocalUser(createFallbackUser(sessionData.data));
            }
          } else {
            if (import.meta.env.DEV) {
              console.log('[Auth] Session response format unexpected:', {
                hasSuccess: sessionData?.success,
                hasUser: !!sessionData?.data?.user,
                hasUserId: !!sessionData?.data?.userId
              });
            }
            setLocalUser(null);
          }
        } else {
          if (import.meta.env.DEV) {
            console.log('[Auth] No valid session found, status:', response.status);
          }
          setLocalUser(null);
          
          // If session validation fails but we have a WorkOS user, clear WorkOS state
          // This prevents the authentication limbo state
          if (user && response.status === 401) {
            if (import.meta.env.DEV) {
              console.log('[Auth] Session invalid but WorkOS user exists, clearing WorkOS state');
            }
            // Force WorkOS SDK to clear its state
            try {
              await signOut();
            } catch (signOutError) {
              if (import.meta.env.DEV) {
                console.warn('[Auth] Failed to sign out from WorkOS:', signOutError);
              }
            }
          }
        }
      } catch (error) {
        // Don't log or update state if the request was aborted
        if (abortController.signal.aborted) {
          if (import.meta.env.DEV) console.log('[Auth] Session restoration fetch was aborted');
          return;
        }
        
        console.error('[Auth] Session restoration failed:', error);
        setLocalUser(null);
        
        // If session restoration fails completely, also clear WorkOS state
        if (user) {
          if (import.meta.env.DEV) {
            console.log('[Auth] Session restoration failed, clearing WorkOS state');
          }
          try {
            await signOut();
          } catch (signOutError) {
            if (import.meta.env.DEV) {
              console.warn('[Auth] Failed to sign out from WorkOS after restoration failure:', signOutError);
            }
          }
        }
      } finally {
        // Only update loading state if not aborted
        if (!abortController.signal.aborted) {
          setIsRestoringSession(false);
        }
      }
    };

    restoreSession();

    // Cleanup function to abort the fetch if component unmounts
    return () => {
      abortController.abort();
    };
  }, [isRestoringSession]); // Only trigger on manual restoration requests

  /**
   * WorkOS User Change Handler Effect
   * 
   * This effect handles changes in the WorkOS user state, including initial
   * login and session changes. It exchanges WorkOS tokens for secure JWT
   * tokens stored in HttpOnly cookies.
   */
  useEffect(() => {
    trackEffect('workos_user_change');
    
    if (!user) {
      if (import.meta.env.DEV) {
        console.log('[Auth] No WorkOS user, skipping user change handler');
      }
      return;
    }

    // If we already have a local user and aren't loading, skip processing
    // This prevents the loop when WorkOS user persists after successful login
    if (localUser && !isRestoringSession) {
      if (import.meta.env.DEV) {
        console.log('[Auth] Already have local user, skipping WorkOS user processing');
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.log('[Auth] WorkOS user object received:', user);
      console.log('[Auth] WorkOS auth state on user change:', workOSAuth);
    }

    const abortController = new AbortController();

    const handleWorkOSUser = async () => {
      try {
        if (import.meta.env.DEV) {
          console.log('[Auth] Processing WorkOS user login');
          console.log('[Auth] User object available:', user);
        }
        
        // Get access token from WorkOS SDK
        if (import.meta.env.DEV) {
          console.log('[Auth] Getting access token from WorkOS SDK');
        }
        const accessToken = await workOSAuth.getAccessToken();
        
        if (!accessToken) {
          throw new Error('No access token available from WorkOS SDK');
        }
        
        if (import.meta.env.DEV) {
          console.log('[Auth] Got access token, exchanging with backend');
        }
        
        // Exchange WorkOS token for secure session cookies
        const tokenExchangeResponse = await fetch(`${API_BASE_URL}/api/auth/workos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ 
            token: accessToken,
            user: {
              id: user.id,
              email: user.email,
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              profile_picture_url: user.profilePictureUrl,
              email_verified: user.emailVerified ?? true
            }
          }),
        });
        
        if (import.meta.env.DEV) {
          console.log('[Auth] Token exchange response:', {
            status: tokenExchangeResponse.status,
            ok: tokenExchangeResponse.ok,
            statusText: tokenExchangeResponse.statusText
          });
        }
        
        if (tokenExchangeResponse.ok) {
          if (import.meta.env.DEV) {
            console.log('[Auth] Token exchange successful, fetching user session');
          }
          
          // Parse response to get user data
          const responseText = await tokenExchangeResponse.text();
          let tokenExchangeData;
          try {
            tokenExchangeData = responseText ? JSON.parse(responseText) : null;
          } catch (parseError) {
            console.warn('[Auth] Failed to parse token exchange response:', parseError);
            tokenExchangeData = null;
          }

          if (tokenExchangeData?.success && tokenExchangeData.data?.user) {
            // Set user directly from token exchange response
            const userData = tokenExchangeData.data.user;
            const normalizedUser: WorkOSUser = {
              id: userData.id,
              email: userData.email,
              firstName: userData.name?.split(' ')[0] || user.firstName,
              lastName: userData.name?.split(' ').slice(1).join(' ') || user.lastName,
              organizations: userData.organizations || [],
              raw_user: user as RawWorkOSUser,
              raw_token: user.raw_token
            };
            
            if (import.meta.env.DEV) {
              console.log('[Auth] Setting user from token exchange response');
            }
            setLocalUser(normalizedUser);
            
            // Identify user in Koala
            identifyUser(normalizedUser.email, {
              name: userData.name || `${normalizedUser.firstName || ''} ${normalizedUser.lastName || ''}`.trim(),
              firstName: normalizedUser.firstName,
              lastName: normalizedUser.lastName,
              organizationId: normalizedUser.organizations?.[0]?.id || user.organization_membership?.organization_id,
              isAuthenticated: true
            });
          } else {
            // Fallback: trigger session restoration to pick up the new cookies
            if (import.meta.env.DEV) {
              console.log('[Auth] Token exchange successful but no user data, triggering session restoration');
            }
            // Force re-run session restoration to pick up new cookies
            setIsRestoringSession(true);
            setTimeout(() => {
              // This will trigger the session restoration effect to re-run
              setIsRestoringSession(false);
            }, 100);
          }
          return;
        } else {
          throw new Error(`Token exchange failed: ${tokenExchangeResponse.status} ${tokenExchangeResponse.statusText}`);
        }
      } catch (error) {
        // Don't update state if the request was aborted
        if (abortController.signal.aborted) {
          if (import.meta.env.DEV) console.log('[Auth] WorkOS user handling was aborted');
          return;
        }
        
        // Handle the specific "Empty response from token exchange" error
        if (error instanceof Error && error.message.includes('Empty response from token exchange')) {
          if (import.meta.env.DEV) {
            console.log('[Auth] Empty response detected, triggering session restoration to pick up cookies');
            console.log('[Auth] Original error was:', error);
          }
          // Trigger session restoration instead of reload
          setIsRestoringSession(true);
          setTimeout(() => {
            setIsRestoringSession(false);
          }, 100);
          return;
        }
        
        console.error('[Auth] WorkOS user handling failed:', error);
        setError(error instanceof Error ? error.message : 'Failed to handle WorkOS user');
        
        // Clear session by calling logout endpoint, but only if component is still mounted
        if (!abortController.signal.aborted) {
          try {
            await fetch(`${API_BASE_URL}/api/auth/logout`, {
              method: 'POST',
              credentials: 'include',
              signal: abortController.signal
            });
            if (import.meta.env.DEV) {
              console.log('[Auth] Cleared session via logout endpoint after error');
            }
          } catch (logoutError) {
            // Log logout failures but don't hide the original error
            if (import.meta.env.DEV) {
              console.warn('[Auth] Failed to clear session via logout endpoint:', logoutError);
            }
          }
        }
        
        setLocalUser(null);
      }
    };

    handleWorkOSUser();

    // Cleanup function to abort the fetch if component unmounts
    return () => {
      abortController.abort();
    };
  }, [user, workOSAuth, localUser, isRestoringSession, isLoading]);

  /**
   * Global Authentication Event Listener
   * 
   * This effect listens for global authentication events that require
   * immediate logout or session invalidation.
   */
  useEffect(() => {
    const handleAuthEvent = async (data: { reason?: string; error?: unknown }) => {
      if (import.meta.env.DEV) {
        console.log('[Auth] Received auth event requiring logout:', data);
      }
      
      // Clear local user state immediately
      setLocalUser(null);
      setError(data.reason || 'Session expired');
      
      // Perform logout cleanup
      try {
        // Clear session cookies via backend
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include' // Include cookies to clear them
        });
        
        // Sign out from WorkOS
        await signOut();
        
        // Clear any residual browser storage
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (storageError) {
          if (import.meta.env.DEV) {
            console.warn('[Auth] Failed to clear browser storage:', storageError);
          }
        }
      } catch (error) {
        console.error('[Auth] Failed to logout after auth event:', error);
      }
    };

    // Subscribe to auth events
    const unsubscribers = [
      authEvents.on(AuthEvent.SESSION_EXPIRED, handleAuthEvent),
      authEvents.on(AuthEvent.SESSION_INVALID, handleAuthEvent),
      authEvents.on(AuthEvent.LOGOUT_REQUIRED, handleAuthEvent),
      authEvents.on(AuthEvent.AUTH_ERROR, handleAuthEvent)
    ];

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [signOut, setLocalUser, setError]);

  /**
   * Periodic Session Validation Effect
   * 
   * This effect periodically validates the session to detect when users
   * go offline or their session expires without making API calls.
   */
  useEffect(() => {
    // Only run periodic validation if user is authenticated
    if (!localUser) {
      return;
    }

    const validateSession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          if (import.meta.env.DEV) {
            console.log('[Auth] Periodic session validation failed:', response.status);
          }
          // Session is invalid, emit event to trigger logout
          authEvents.emit(AuthEvent.SESSION_INVALID, {
            reason: 'Periodic session validation failed'
          });
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.log('[Auth] Periodic session validation error:', error);
        }
        // Network error - user might be offline
        // Don't immediately logout on network errors, just log it
      }
    };

    // Validate session every 5 minutes
    const intervalId = setInterval(validateSession, 5 * 60 * 1000);

    // Also validate on window focus to catch session expiry while tab was inactive
    const handleFocus = () => {
      validateSession();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [localUser]);

  /**
   * Authentication State Effect
   * 
   * This effect manages the final authentication state by combining
   * WorkOS user state and local session state.
   */
  useEffect(() => {
    trackEffect('auth_state_management');
    
    // Wait for both session restoration and WorkOS SDK loading to finish
    if (isRestoringSession || isLoading) {
      if (import.meta.env.DEV) {
        console.log('[Auth] Waiting for initialization:', { isRestoringSession, isLoading });
      }
      return;
    }

    // If localUser exists and user is null, maintain session (do nothing)
    if (localUser) {
      if (import.meta.env.DEV) {
        console.log('[Auth] Local user exists, maintaining session');
      }
      return;
    }

    // If we have a WorkOS user but no local user after initialization,
    // this indicates the WorkOS SDK has user data but our session restoration missed it
    // Log this condition but don't trigger another restoration to avoid infinite loops
    if (user && !localUser) {
      if (import.meta.env.DEV) {
        console.log('[Auth] WorkOS user exists but no local user - potential sync issue');
      }
      // Don't trigger session restoration here to avoid infinite loops
      // The WorkOS user change handler effect should handle this case
      return;
    }

    // If no local user and initialization is complete, user is not authenticated
    if (import.meta.env.DEV) {
      console.log('[Auth] No user found after initialization, user not authenticated');
    }
  }, [user, isRestoringSession, isLoading, localUser]);

  /**
   * Login function
   * 
   * Initiates the WorkOS authentication flow
   */
  const login = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log('[Auth] Login button clicked - initiating WorkOS signin');
      console.log('[Auth] Current auth state before login:', {
        hasUser: !!user,
        hasLocalUser: !!localUser,
        isLoading,
        isRestoringSession
      });
    }
    signIn();
  }, [signIn, user, localUser, isLoading, isRestoringSession]);

  /**
   * Logout function
   * 
   * Clears the local user state and removes secure session cookies
   * by calling the backend logout endpoint
   */
  const logout = useCallback(async () => {
    try {
      // Clear local state first for immediate UI feedback
      setLocalUser(null);
      
      // Parallelize independent logout operations to reduce perceived latency
      const logoutOperations = await Promise.allSettled([
        // Clear session cookies via backend
        fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include' // Include cookies to clear them
        }),
        // Sign out from WorkOS
        signOut()
      ]);

      // Log any failures but don't throw - logout should always appear to succeed
      logoutOperations.forEach((result, index) => {
        if (result.status === 'rejected') {
          const operation = index === 0 ? 'backend logout' : 'WorkOS signOut';
          if (import.meta.env.DEV) {
            console.warn(`[Auth] ${operation} failed:`, result.reason);
          }
        }
      });

      // Clear any residual browser storage
      try {
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear all cookies for this domain
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos) : c;
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
      } catch (storageError) {
        if (import.meta.env.DEV) {
          console.warn('[Auth] Failed to clear browser storage:', storageError);
        }
      }

      if (import.meta.env.DEV) {
        console.log('[Auth] Logout completed with full cleanup');
      }
    } catch (error) {
      // Log error but don't throw - logout should always appear to succeed from user perspective
      console.error('[Auth] Logout error:', error);
    }
  }, [signOut]);

  const contextValue: AuthContextType = useMemo(() => ({
    user: localUser,
    isAuthenticated: !!localUser,
    loading: isRestoringSession || isLoading || (!!user && !localUser), // Add WorkOS user processing state
    login,
    logout,
    error
  }), [localUser, isRestoringSession, isLoading, user, login, logout, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth Hook
 * 
 * Custom hook to access the authentication context
 * 
 * @returns {AuthContextType} The authentication context
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};